import { useState, useMemo } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useUser } from '@/contexts/UserContext';
import { useShipments } from '@/hooks/useShipments';
import { useCustomers } from '@/hooks/useCustomers';
import { useWarehouses } from '@/hooks/useWarehouses';
import { useInvoices } from '@/hooks/useInvoices';
import {
  Download,
  Printer,
  TrendingUp,
  Calendar,
  Filter,
  DollarSign,
  Package,
  Users,
  Building2,
  BarChart3,
  FileText
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export default function Reports() {
  const { user } = useUser();
  const { shipments: apiShipments } = useShipments();
  const { customers: apiCustomers } = useCustomers();
  const { warehouses: apiWarehouses } = useWarehouses();
  const { invoices: rawInvoices } = useInvoices();

  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');

  const shipments = apiShipments || [];
  const customers = apiCustomers || [];
  const warehouses = apiWarehouses || [];
  const invoices = (rawInvoices || []).map((inv: any) => ({
    ...inv,
    invoiceNumber: inv.invoice_number,
    customerCode: inv.customer_code,
    customerName: inv.customer_name,
    total: inv.total || 0,
  }));

  // Filter data by date range
  const filteredData = useMemo(() => {
    let filtered = { shipments, invoices };

    if (dateRange.startDate && dateRange.endDate) {
      const start = new Date(dateRange.startDate);
      const end = new Date(dateRange.endDate);

      filtered.shipments = shipments.filter((s: any) => {
        const date = new Date(s.createdAt || s.created_at);
        return date >= start && date <= end;
      });

      filtered.invoices = invoices.filter((inv: any) => {
        const date = new Date(inv.createdAt || inv.created_at);
        return date >= start && date <= end;
      });
    }

    if (selectedWarehouse !== 'all') {
      filtered.shipments = filtered.shipments.filter(
        (s: any) => s.originWarehouse === selectedWarehouse || s.destinationWarehouse === selectedWarehouse
      );
    }

    if (selectedStatus !== 'all') {
      filtered.shipments = filtered.shipments.filter((s: any) => s.status === selectedStatus);
    }

    return filtered;
  }, [shipments, invoices, dateRange, selectedWarehouse, selectedStatus]);

  // Revenue over time (monthly)
  const revenueData = useMemo(() => {
    const monthlyRevenue: { [key: string]: number } = {};

    filteredData.invoices.forEach((inv: any) => {
      const date = new Date(inv.createdAt || inv.created_at);
      const month = date.toLocaleString('default', { month: 'short', year: 'numeric' });

      if (!monthlyRevenue[month]) {
        monthlyRevenue[month] = 0;
      }

      if (inv.status === 'Paid' || inv.status === 'Partial') {
        monthlyRevenue[month] += parseFloat(inv.total) || 0;
      }
    });

    return Object.entries(monthlyRevenue)
      .map(([month, revenue]) => ({ month, revenue }))
      .slice(-6); // Last 6 months
  }, [filteredData.invoices]);

  // Top customers by shipment count
  const topCustomers = useMemo(() => {
    const customerShipments: { [key: string]: { name: string; count: number } } = {};

    filteredData.shipments.forEach((s: any) => {
      const code = s.customerCode;
      if (!customerShipments[code]) {
        customerShipments[code] = { name: s.customerName || code, count: 0 };
      }
      customerShipments[code].count++;
    });

    return Object.entries(customerShipments)
      .map(([code, data]) => ({ code, ...data }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Top 10
  }, [filteredData.shipments]);

  // Warehouse performance
  const warehousePerformance = useMemo(() => {
    const performance: { [key: string]: { name: string; shipments: number; revenue: number } } = {};

    filteredData.shipments.forEach((s: any) => {
      const code = s.originWarehouse;
      const warehouse = warehouses.find((w: any) => w.code === code);
      const name = warehouse?.name || code;

      if (!performance[code]) {
        performance[code] = { name, shipments: 0, revenue: 0 };
      }
      performance[code].shipments++;
    });

    filteredData.invoices.forEach((inv: any) => {
      // Find warehouse from customer's destination or invoice metadata
      const warehouse = warehouses[0]?.code || 'Unknown';
      if (performance[warehouse]) {
        performance[warehouse].revenue += parseFloat(inv.total) || 0;
      }
    });

    return Object.entries(performance)
      .map(([code, data]) => ({ code, ...data }));
  }, [filteredData.shipments, filteredData.invoices, warehouses]);

  // Shipments by status
  const shipmentsByStatus = useMemo(() => {
    const statusCount: { [key: string]: number } = {};

    filteredData.shipments.forEach((s: any) => {
      const status = s.status || 'Unknown';
      statusCount[status] = (statusCount[status] || 0) + 1;
    });

    return Object.entries(statusCount).map(([status, count]) => ({ status, count }));
  }, [filteredData.shipments]);

  // Export all reports to PDF
  const exportAllToPDF = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Complete Reports - ${new Date().toLocaleDateString()}</title>
        <style>
          @page {
            size: A4;
            margin: 15mm;
          }
          
          @media print {
            body { padding: 0; margin: 0; }
          }
          
          body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            padding: 20px;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #2563eb;
            padding-bottom: 20px;
          }
          h1 { color: #2563eb; margin: 0; }
          h2 { color: #374151; margin-top: 30px; }
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
          }
          th, td {
            border: 1px solid #e5e7eb;
            padding: 10px;
            text-align: left;
          }
          th {
            background-color: #f3f4f6;
            font-weight: bold;
          }
          .footer {
            margin-top: 40px;
            text-align: center;
            font-size: 12px;
            color: #6b7280;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>SourceMyGoods - Complete Reports</h1>
          <p>Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
          ${dateRange.startDate && dateRange.endDate ? `<p>Date Range: ${dateRange.startDate} to ${dateRange.endDate}</p>` : ''}
        </div>

        <h2>Revenue Over Time</h2>
        <table>
          <thead>
            <tr><th>Month</th><th>Revenue</th></tr>
          </thead>
          <tbody>
            ${revenueData.map(d => `<tr><td>${d.month}</td><td>$${d.revenue.toFixed(2)}</td></tr>`).join('')}
          </tbody>
        </table>

        <h2>Top 10 Customers</h2>
        <table>
          <thead>
            <tr><th>Customer Code</th><th>Name</th><th>Shipments</th></tr>
          </thead>
          <tbody>
            ${topCustomers.map(c => `<tr><td>${c.code}</td><td>${c.name}</td><td>${c.count}</td></tr>`).join('')}
          </tbody>
        </table>

        <h2>Warehouse Performance</h2>
        <table>
          <thead>
            <tr><th>Warehouse</th><th>Shipments</th><th>Revenue</th></tr>
          </thead>
          <tbody>
            ${warehousePerformance.map(w => `<tr><td>${w.name}</td><td>${w.shipments}</td><td>$${w.revenue.toFixed(2)}</td></tr>`).join('')}
          </tbody>
        </table>

        <h2>Shipments by Status</h2>
        <table>
          <thead>
            <tr><th>Status</th><th>Count</th></tr>
          </thead>
          <tbody>
            ${shipmentsByStatus.map(s => `<tr><td>${s.status}</td><td>${s.count}</td></tr>`).join('')}
          </tbody>
        </table>

        <div class="footer">
          SourceMyGoods - Professional Sourcing, Inspection & Global Shipping
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Export to Excel (CSV)
  const exportToExcel = () => {
    const csvData = [
      ['SourceMyGoods - Complete Reports'],
      [`Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`],
      dateRange.startDate && dateRange.endDate ? [`Date Range: ${dateRange.startDate} to ${dateRange.endDate}`] : [],
      [],
      ['Revenue Over Time'],
      ['Month', 'Revenue'],
      ...revenueData.map(d => [d.month, d.revenue.toFixed(2)]),
      [],
      ['Top 10 Customers'],
      ['Customer Code', 'Name', 'Shipments'],
      ...topCustomers.map(c => [c.code, c.name, c.count]),
      [],
      ['Warehouse Performance'],
      ['Warehouse', 'Shipments', 'Revenue'],
      ...warehousePerformance.map(w => [w.name, w.shipments, w.revenue.toFixed(2)]),
      [],
      ['Shipments by Status'],
      ['Status', 'Count'],
      ...shipmentsByStatus.map(s => [s.status, s.count]),
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `complete-reports-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
            <p className="text-gray-600">Comprehensive business insights and data analysis</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={exportAllToPDF}
              className="flex items-center gap-2"
            >
              <Printer className="h-4 w-4" />
              Export All (PDF)
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportToExcel}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export All (Excel)
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
            </CardTitle>
            <CardDescription>Customize report data by date range, warehouse, and status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Warehouse</Label>
                <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Warehouses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Warehouses</SelectItem>
                    {warehouses.map((w: any) => (
                      <SelectItem key={w.code} value={w.code}>{w.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="Processing">Processing</SelectItem>
                    <SelectItem value="In Transit">In Transit</SelectItem>
                    <SelectItem value="At Destination">At Destination</SelectItem>
                    <SelectItem value="Delivered">Delivered</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setDateRange({ startDate: '', endDate: '' });
                  setSelectedWarehouse('all');
                  setSelectedStatus('all');
                }}
              >
                Clear All Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Revenue Over Time Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              Revenue Over Time
            </CardTitle>
            <CardDescription>Monthly revenue trends (last 6 months)</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => `$${Number(value).toFixed(2)}`} />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} name="Revenue" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Customers Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-600" />
                Top 10 Customers
              </CardTitle>
              <CardDescription>Ranked by shipment count</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topCustomers}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="code" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#8b5cf6" name="Shipments" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Shipments by Status Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5 text-blue-600" />
                Shipments by Status
              </CardTitle>
              <CardDescription>Distribution of shipment statuses</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={shipmentsByStatus}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ status, percent }) => `${status} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {shipmentsByStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Warehouse Performance Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-orange-600" />
              Warehouse Performance Comparison
            </CardTitle>
            <CardDescription>Shipment volume and revenue by warehouse</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-semibold">Warehouse</th>
                    <th className="text-right p-3 font-semibold">Total Shipments</th>
                    <th className="text-right p-3 font-semibold">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {warehousePerformance.map((w) => (
                    <tr key={w.code} className="border-b hover:bg-gray-50">
                      <td className="p-3">{w.name}</td>
                      <td className="text-right p-3">{w.shipments}</td>
                      <td className="text-right p-3 font-medium">${w.revenue.toFixed(2)}</td>
                    </tr>
                  ))}
                  {warehousePerformance.length === 0 && (
                    <tr>
                      <td colSpan={3} className="text-center p-6 text-gray-500">
                        No data available for selected filters
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-t-4 border-t-blue-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-700">Total Shipments</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{filteredData.shipments.length}</div>
              <p className="text-xs text-gray-500 mt-1">In selected period</p>
            </CardContent>
          </Card>

          <Card className="border-t-4 border-t-green-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-700">Total Revenue</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                ${revenueData.reduce((sum, d) => sum + d.revenue, 0).toFixed(2)}
              </div>
              <p className="text-xs text-gray-500 mt-1">Last 6 months</p>
            </CardContent>
          </Card>

          <Card className="border-t-4 border-t-purple-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-700">Active Customers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{topCustomers.length}</div>
              <p className="text-xs text-gray-500 mt-1">With shipments</p>
            </CardContent>
          </Card>

          <Card className="border-t-4 border-t-orange-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-700">Avg. Revenue/Customer</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                ${topCustomers.length > 0 
                  ? (revenueData.reduce((sum, d) => sum + d.revenue, 0) / topCustomers.length).toFixed(2)
                  : '0.00'
                }
              </div>
              <p className="text-xs text-gray-500 mt-1">Per customer</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
